import { useEffect } from "react";
import { useRouteError, isRouteErrorResponse } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { Throbber } from "@lytra/frontend-common";

/**
 * Render an error of unknown shape as text. Never throws — this component is
 * already the last line of defence, so a failure here leaves a blank page.
 */
function describeError(error: unknown): string | null {
  if (error == null) return null;
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
  if (error instanceof Error) return `${error.message}\n${error.stack ?? ""}`;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return "Fehler konnte nicht dargestellt werden";
  }
}

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Importing a module script failed") ||
      error.message.includes("error loading dynamically imported module") ||
      error.message.includes("Loading module from") ||
      error.message.includes("Loading chunk") ||
      error.message.includes("ChunkLoadError")
    );
  }
  return false;
}

export default function RouteErrorBoundary() {
  const error = useRouteError();
  const isChunkError = isChunkLoadError(error);
  const errorDetails = describeError(error);

  useEffect(() => {
    if (isChunkError) {
      // Auto-reload after a short delay so the user sees the message briefly
      const timer = setTimeout(() => {
        window.location.reload();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isChunkError]);

  if (isChunkError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <Alert className="max-w-md shadow-lg">
          <Throbber className="h-5 w-5 mt-1" />
          <AlertTitle className="text-lg font-semibold">
            Neue Version verfuegbar
          </AlertTitle>
          <AlertDescription>
            <p className="mt-2 text-sm text-foreground">
              Eine neue Version der Anwendung wurde bereitgestellt. Die Seite
              wird automatisch neu geladen...
            </p>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // For non-chunk errors, show a generic error page
  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <Alert variant="destructive" className="max-w-xl shadow-lg">
        <AlertTriangle className="h-5 w-5 mt-1" />
        <AlertTitle className="text-lg font-semibold">
          Etwas ist schiefgelaufen
        </AlertTitle>
        <AlertDescription>
          <div className="mt-2">
            <p className="text-sm text-foreground">
              Ein unerwarteter Fehler ist aufgetreten.
            </p>
            {import.meta.env.DEV && errorDetails && (
              <pre className="mt-3 p-2 bg-muted rounded text-xs overflow-auto max-h-40 whitespace-pre-wrap">
                {errorDetails}
              </pre>
            )}
            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => window.location.reload()}
                variant="default"
              >
                Seite neu laden
              </Button>
              <Button
                variant="outline"
                onClick={() => (window.location.href = "/")}
                className="text-foreground"
              >
                Zur Startseite
              </Button>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}
